import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  deleteToolSharedUser,
  getToolByWorkflow,
  getToolSharedUsers,
  getToolView,
  setToolVisibility,
  setWorkflowToolAudience,
  shareTool,
  ToolByWorkflow,
  ToolSharedUser,
  ToolView,
} from '@/api/toolView.api';
import { getToolRunData, getToolUserData, ToolRunData } from '@/api/toolRunData.api';
import { useWorkflowStore } from '@/state/workflow.state';
import { useGlobalStore } from '@/state/global.state';
import { useAnalytics, TrackTypeEnum } from '@/hooks/useAnalytics';
import { I18N_KEYS } from '@/language/keys';
import type { ToolVisibilityType } from '@/enums/tool-visibility-type';
import type { DesignAppData } from '@/types/design-app.types';

const TOOL_VIEW_KEY = 'tool-view';
const TOOL_SHARED_USERS_KEY = 'tool-shared-users';
const TOOL_BY_WORKFLOW_KEY = 'tool-by-workflow';
const TOOL_RUN_DATA_KEY = 'tool-run-data';
const TOOL_USER_DATA_KEY = 'tool-user-data';

/** Exported so callers can read the freshest cached {@link ToolView} instead of a render-time snapshot. */
export const toolViewQueryKey = (toolId: string | undefined) => [TOOL_VIEW_KEY, toolId] as const;

export const useToolViewQuery = (toolId: string | undefined, enabled = true) =>
  useQuery<ToolView>({
    queryKey: toolViewQueryKey(toolId),
    queryFn: () => getToolView(toolId!),
    enabled: !!toolId && enabled,
    retry: false, // 403/404/409 are terminal states the page renders explicitly
  });

export const useToolSharedUsersQuery = (toolId: string | undefined, enabled = true) =>
  useQuery<{ users: ToolSharedUser[] }>({
    queryKey: [TOOL_SHARED_USERS_KEY, toolId],
    queryFn: () => getToolSharedUsers(toolId!),
    enabled: !!toolId && enabled,
  });

export const useShareToolMutation = (toolId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (emails: string[]) => shareTool(toolId, emails),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [TOOL_SHARED_USERS_KEY, toolId] });
    },
  });
};

export const useSetToolVisibilityMutation = (toolId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (visibility: ToolVisibilityType) => setToolVisibility(toolId, visibility),
    // Optimistic for instant feedback, then reconcile on settle. getToolView reads the primary, so the
    // refetch returns the fresh value (no stale-replica revert). Roll back on error.
    onMutate: async (visibility) => {
      await queryClient.cancelQueries({ queryKey: toolViewQueryKey(toolId) });
      const previous = queryClient.getQueryData<ToolView>(toolViewQueryKey(toolId));
      queryClient.setQueryData<ToolView>(toolViewQueryKey(toolId), (old) => (old ? { ...old, visibility } : old));
      return { previous };
    },
    onError: (_err, _visibility, context) => {
      if (context?.previous) queryClient.setQueryData(toolViewQueryKey(toolId), context.previous);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: toolViewQueryKey(toolId) });
    },
  });
};

export const useDeleteToolSharedUserMutation = (toolId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (email: string) => deleteToolSharedUser(toolId, email),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [TOOL_SHARED_USERS_KEY, toolId] });
    },
  });
};

export const useInvalidateToolByWorkflow = () => {
  const queryClient = useQueryClient();
  return useCallback(
    (workflowId: string) => queryClient.invalidateQueries({ queryKey: [TOOL_BY_WORKFLOW_KEY, workflowId] }),
    [queryClient],
  );
};

export type ToolAudienceSurface = 'tool_details' | 'create_tool_modal';

/**
 * The one writer for "show the workflow to the tool's audience", shared by Tool details and the create
 * modal. Optimistic on the workflow store, rolled back with the generic save error on failure. The
 * effect lands on the tool's own page: its viewers get Go to workflow through `canViewWorkflow`.
 */
export const useSetWorkflowToolAudienceMutation = (workflowId: string) => {
  const { t } = useTranslation();
  const { track } = useAnalytics();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ includesToolAudience }: { includesToolAudience: boolean; surface: ToolAudienceSurface }) =>
      setWorkflowToolAudience(workflowId, includesToolAudience),
    onMutate: ({ includesToolAudience }) => {
      const { recipe, setRecipe } = useWorkflowStore.getState();
      if (recipe.id !== workflowId) {
        return { previous: undefined };
      }
      setRecipe({ ...recipe, visibilityIncludesToolAudience: includesToolAudience });
      return { previous: recipe.visibilityIncludesToolAudience };
    },
    // A tool's id is its workflow's, and its view carries the opt-in for the Share modal off the editor.
    onSettled: () => queryClient.invalidateQueries({ queryKey: toolViewQueryKey(workflowId) }),
    onError: (_error, _variables, context) => {
      const { recipe, setRecipe } = useWorkflowStore.getState();
      if (recipe.id === workflowId) {
        setRecipe({ ...recipe, visibilityIncludesToolAudience: context?.previous });
      }
      useGlobalStore.getState().updateSnackbarData({
        text: t(I18N_KEYS.RECIPE_MAIN.ERRORS.SAVE_ERRORS.GENERAL.MESSAGE),
        isOpen: true,
        icon: null,
        severity: 'error',
      });
    },
    onSuccess: (_data, { includesToolAudience, surface }) => {
      track(
        'workflow_tool_audience_toggled',
        {
          enabled: includesToolAudience,
          surface,
          workflow_id: workflowId,
          // App View Separation: tool id is the workflow id.
          tool_id: workflowId,
        },
        TrackTypeEnum.BI,
      );
    },
  });
};

export const useToolByWorkflowQuery = (workflowId: string | undefined, enabled: boolean) =>
  useQuery<ToolByWorkflow | null>({
    queryKey: [TOOL_BY_WORKFLOW_KEY, workflowId],
    queryFn: () => getToolByWorkflow(workflowId!),
    enabled: !!workflowId && enabled,
    retry: false,
    // Creating the tool from the Preview tab is this query's only other writer.
    refetchOnWindowFocus: true,
  });

/** `toolId` is optional so a caller can observe the runner's load without triggering it early. */
export const useToolRunDataQuery = (toolId: string | undefined, version?: number) =>
  useQuery<ToolRunData>({
    queryKey: [TOOL_RUN_DATA_KEY, toolId, version],
    queryFn: () => getToolRunData(toolId!, version),
    enabled: !!toolId,
    retry: false,
  });

export const useToolUserDataQuery = (toolId: string, version: number | undefined, enabled: boolean) =>
  useQuery<DesignAppData>({
    queryKey: [TOOL_USER_DATA_KEY, toolId, version],
    queryFn: () => getToolUserData(toolId, version!),
    enabled: enabled && version !== undefined,
    retry: false,
  });
